/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Entity } from '@backstage/catalog-model';
import { useEffect, useMemo } from 'react';
import { pickBy } from 'lodash';
import { useEntityStore } from './useEntityStore';

type UseEntityRelationGraphOptions = {
  rootEntityRefs: string[];
  filter?: {
    maxDepth?: number;
    relations?: string[];
    kinds?: string[];
    entityFilter?: (entity: Entity) => boolean;
  };
};

export const useEntityRelationGraph = ({
  rootEntityRefs,
  filter: {
    maxDepth = Number.POSITIVE_INFINITY,
    relations,
    kinds,
    entityFilter,
  } = {},
}: UseEntityRelationGraphOptions): {
  entities?: Record<string, Entity>;
  loading: boolean;
  error?: Error;
} => {
  const { entities, loading, error, requestEntities } = useEntityStore();

  useEffect(() => {
    const expected = new Set(rootEntityRefs);
    const processedRefs = new Set<string>();

    let nextDepthRefQueue = [...rootEntityRefs];
    let depth = 0;

    while (
      nextDepthRefQueue.length > 0 &&
      (!Number.isFinite(maxDepth) || depth < maxDepth)
    ) {
      const entityRefQueue = nextDepthRefQueue;
      nextDepthRefQueue = [];

      while (entityRefQueue.length > 0) {
        const entityRef = entityRefQueue.shift()!;
        const entity = entities[entityRef];

        processedRefs.add(entityRef);

        if (!entity?.relations) {
          continue;
        }

        if (entityFilter && !entityFilter(entity)) {
          continue;
        }

        for (const rel of entity.relations) {
          const relationAllowed =
            !relations || relations.includes(rel.type);
          const kindAllowed =
            !kinds ||
            kinds.some(kind =>
              rel.targetRef.startsWith(
                `${kind.toLocaleLowerCase('en-US')}:`,
              ),
            );

          if (!relationAllowed || !kindAllowed) {
            continue;
          }

          if (!processedRefs.has(rel.targetRef)) {
            nextDepthRefQueue.push(rel.targetRef);
            expected.add(rel.targetRef);
          }
        }
      }

      depth += 1;
    }

    requestEntities(Array.from(expected));
  }, [
    entities,
    entityFilter,
    kinds,
    maxDepth,
    relations,
    requestEntities,
    rootEntityRefs,
  ]);

  const filteredEntities = useMemo(() => {
    if (!entityFilter) {
      return entities;
    }

    return pickBy(entities, entity => entityFilter(entity));
  }, [entities, entityFilter]);

  return {
    entities: filteredEntities,
    loading,
    error,
  };
};

